import { useEffect } from "react";
import { Phone, Check, ArrowRight } from "lucide-react";
import { SERVICES_PAGES, servicePath } from "../lib/services";
import SeoPageShell, { PHONE, PHONE_HREF } from "../components/SeoPageShell";

const PRICE_ROWS = [
  { job: "Single item (couch, mattress, fridge)", price: "$249", note: "Our minimum pickup — covers crew, hauling & disposal" },
  { job: "Small room (sofa, table, the usual suspects)", price: "From $549", note: "About a quarter truckload — our most-booked pickup" },
  { job: "Garage cleanout or bedroom set", price: "From $849", note: "About a half truckload, sorted on site: donate, recycle, dispose" },
  { job: "Full cleanout (whole-home, basement, estate)", price: "From $1,100", note: "Full truck and up — firm-quoted from your photos before we arrive" },
];

const FAQS = [
  { q: "How much does junk removal cost?", a: "Junk removal with milla starts at $249 for a single-item pickup and scales with volume — a small room's worth starts at $549 (about a quarter truckload), a garage cleanout starts at $849 (about a half truckload), and full whole-home cleanouts start at $1,100. Unlike truck-volume pricing decided on your driveway, your quote is item-based and firm: snap a photo and see your exact price before booking." },
  { q: "Why do most junk removal companies not show prices?", a: "Most haulers price by how much of their truck you fill, decided by the crew on your driveway — which is why they only give ranges until they arrive. milla prices by item from your photo, so the number you see online is the number you pay." },
  { q: "Are there any hidden fees?", a: "No. Your quote includes labor, stairs, hauling, disposal fees, and donation drop-offs. No fuel surcharges, no rush fees for early windows, no surprises on pickup day." },
  { q: "Is junk removal cheaper than renting a dumpster?", a: "For most homeowners, yes. A small dumpster rental runs $300–500 before permit costs — and you still do all the lifting. Junk removal includes the labor, takes one visit instead of a week of driveway blocking, and usable items get donated instead of landfilled." },
];

function setMeta() {
  document.title = "How Much Does Junk Removal Cost? | milla — Firm Prices From $249";
  document.querySelector('meta[name="description"]')?.setAttribute("content", "Junk removal costs explained: single items from $249, small rooms from $549, garage cleanouts from $849, full cleanouts from $1,100. Firm photo quotes, no hidden fees, for Massachusetts & New Hampshire.");
  document.querySelector('link[rel="canonical"]')?.setAttribute("href", "https://usemilla.com/junk-removal-cost");
  const ld = document.createElement("script");
  ld.type = "application/ld+json";
  ld.id = "cost-jsonld";
  ld.textContent = JSON.stringify({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
  });
  document.head.appendChild(ld);
  return () => document.getElementById("cost-jsonld")?.remove();
}

export default function CostGuidePage() {
  useEffect(() => setMeta(), []);

  return (
    <SeoPageShell testid="cost-guide-page">
      {(onStart) => (
        <main className="mx-auto max-w-7xl px-5 sm:px-8">
          <section className="pt-16 pb-12 sm:pt-24 sm:pb-14">
            <p className="inline-flex items-center gap-2 rounded-full bg-lavender px-4 py-1.5 text-sm font-semibold text-violet">Pricing guide</p>
            <h1 className="mt-6 max-w-3xl font-display text-4xl leading-[1.05] text-charcoal sm:text-5xl lg:text-6xl" data-testid="cost-h1">
              How much does junk removal cost?
            </h1>
            <p className="mt-6 max-w-2xl text-lg leading-relaxed text-charcoal/60">
              Short answer: with milla, pickups start at <strong className="text-charcoal">$249</strong> and every quote is firm before you book.
              Here's what real jobs cost in Massachusetts and New Hampshire — no "it depends," no waiting for a crew to price your driveway.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <button onClick={onStart} data-testid="cost-hero-cta" className="inline-flex items-center gap-2 rounded-full bg-violet px-8 py-4 text-base font-semibold text-white transition-all duration-300 hover:bg-violet-deep hover:-translate-y-0.5">
                See your exact price <ArrowRight size={18} />
              </button>
              <a href={PHONE_HREF} className="inline-flex items-center gap-2 rounded-full bg-white px-8 py-4 text-base font-semibold text-charcoal ring-1 ring-charcoal/10 transition-colors hover:bg-lavender hover:text-violet">
                <Phone size={18} /> {PHONE}
              </a>
            </div>
          </section>

          <section className="pb-16">
            <div className="overflow-hidden rounded-[2rem] bg-white/70 ring-1 ring-charcoal/5">
              <table className="w-full text-left" data-testid="cost-table">
                <thead>
                  <tr className="border-b border-charcoal/5 text-sm text-charcoal/45">
                    <th className="px-6 py-4 font-semibold uppercase tracking-wide">Typical job</th>
                    <th className="px-6 py-4 font-semibold uppercase tracking-wide">Price</th>
                    <th className="hidden px-6 py-4 font-semibold uppercase tracking-wide sm:table-cell">What's included</th>
                  </tr>
                </thead>
                <tbody>
                  {PRICE_ROWS.map((r) => (
                    <tr key={r.job} className="border-b border-charcoal/5 last:border-0">
                      <td className="px-6 py-4 text-sm font-medium text-charcoal/80">{r.job}</td>
                      <td className="px-6 py-4 font-display text-lg text-violet">{r.price}</td>
                      <td className="hidden px-6 py-4 text-sm text-charcoal/55 sm:table-cell">{r.note}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-4 text-sm text-charcoal/50">
              Every price includes labor, stairs, hauling, disposal fees, and donation drop-offs. Snap a photo for your exact firm quote.
            </p>
          </section>

          <section className="pb-16">
            <h2 className="font-display text-lg text-charcoal">What makes milla pricing different</h2>
            <div className="mt-5 grid gap-5 sm:grid-cols-3">
              {[
                { t: "Item-based, not truck-based", d: "Most haulers charge by how much of their truck you fill — decided on your driveway. We price each item from your photo, so the quote can't grow on pickup day." },
                { t: "Firm before you book", d: "The price you see is the price you pay. No estimate ranges, no on-site renegotiation, no fuel or rush fees." },
                { t: "Donation-first savings", d: "Usable items go to local donation partners instead of paid disposal — efficiency we build into keeping your price down." },
              ].map((c) => (
                <div key={c.t} className="rounded-[2rem] bg-white/70 p-7 ring-1 ring-charcoal/5">
                  <p className="inline-flex items-center gap-2 font-semibold text-charcoal"><Check size={16} className="text-violet" /> {c.t}</p>
                  <p className="mt-3 text-sm leading-relaxed text-charcoal/60">{c.d}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="pb-16">
            <h2 className="font-display text-lg text-charcoal">Junk removal cost FAQs</h2>
            <div className="mt-5 grid gap-5 lg:grid-cols-2">
              {FAQS.map((f) => (
                <div key={f.q} className="rounded-[2rem] bg-white/70 p-7 ring-1 ring-charcoal/5">
                  <h3 className="font-semibold text-charcoal">{f.q}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-charcoal/60">{f.a}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="pb-16">
            <div className="rounded-[2.5rem] bg-charcoal px-8 py-12 text-canvas sm:px-14">
              <h2 className="font-display text-2xl text-white sm:text-3xl">Stop guessing. See your price.</h2>
              <p className="mt-3 max-w-xl text-canvas/60">One photo gets you a firm, bookable quote in under a minute.</p>
              <button onClick={onStart} data-testid="cost-bottom-cta" className="mt-7 inline-flex items-center gap-2 rounded-full bg-violet px-8 py-3.5 text-sm font-semibold text-white transition-all duration-300 hover:bg-violet-deep">
                Get an instant quote <ArrowRight size={16} />
              </button>
            </div>
          </section>

          <section className="pb-16">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-charcoal/40">Popular services</h2>
            <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2">
              {SERVICES_PAGES.map((s) => (
                <a key={s.slug} href={servicePath(s)} className="text-sm text-charcoal/60 underline-offset-4 transition-colors hover:text-violet hover:underline">{s.label}</a>
              ))}
              <a href="/" className="text-sm font-semibold text-violet underline-offset-4 hover:underline">Home →</a>
            </div>
          </section>
        </main>
      )}
    </SeoPageShell>
  );
}
